import { filter, includes, isEmpty, last, map, toNumber } from 'lodash';
import { TFunction } from 'i18next';

import {
  contractsConfiguration,
  getDropdownContractOptions,
  type ContractConfigNode,
  type ContractOption,
} from '@/utils/options/contractOptions';

export const isContractAllowed =
  (userContracts: number[]) => (contract: ContractOption) =>
    includes(userContracts, toNumber(contract.value));

export const filterOptionsByPermissionsRecursive = (
  options: ContractOption[],
  userContracts: number[],
  level: number = 0
): ContractOption[] =>
  level === 2
    ? filter(options, isContractAllowed(userContracts))
    : filter(
        map(options, (option) =>
          !!option.children
            ? {
                ...option,
                children: filterOptionsByPermissionsRecursive(
                  option.children,
                  userContracts,
                  level + 1
                ),
              }
            : option
        ),
        (option) => !!option.children && !isEmpty(option.children)
      );

/**
 * The Country → City → Contract cascader tree, pruned to the contracts the
 * user is actually granted. Empty in, empty out: a user with no contract may
 * pick nothing, so callers must handle an empty tree rather than falling back
 * to the unrestricted list.
 *
 * Single source for every consumer that needs permission-pruned contract
 * options, so two screens cannot drift into offering different contracts.
 */
export const getPermittedContractOptions = (
  t: TFunction,
  userContracts: number[]
): ContractOption[] =>
  filterOptionsByPermissionsRecursive(
    getDropdownContractOptions(t),
    userContracts
  );

const getAllLeafPaths = (
  nodes: ContractConfigNode[],
  currentPath: string[] = []
): string[][] =>
  nodes.flatMap((node) => {
    const path = [...currentPath, node.id];
    return node.children?.length
      ? getAllLeafPaths(node.children, path)
      : [path];
  });

export const getAllContractLeafPaths = (): string[][] =>
  getAllLeafPaths(contractsConfiguration);

export const getAllowedContractPaths = (userContracts: number[]): string[][] =>
  getAllLeafPaths(contractsConfiguration).filter((path) => {
    const leaf = last(path);
    return leaf !== undefined && includes(userContracts, toNumber(leaf));
  });
